import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Grid
} from '@material-ui/core';
import { StarIcon } from "@heroicons/react/24/outline";

import { useStyles } from './styles';
import { iconRegistry, iconPickerConfig } from './queries';
import type { IconRendererProps, IconPickerProps, IconPickerDialogProps } from './queries';

export const IconRenderer: React.FC<IconRendererProps> = ({ icon, className, style }) => {
  const iconItem = iconRegistry.find(item => item.name === icon);
  
  if (!iconItem) {
    return <StarIcon className={className} style={style} />;
  }

  const { Component } = iconItem;
  return <Component className={className} style={style} />;
};

const useIconPicker = () => {
  const [search, setSearch] = useState('');

  const icons = useMemo(() => {
    if (!search.trim()) {
      return iconRegistry;
    }

    return iconRegistry.filter(icon =>
      icon.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  return {
    search,
    setSearch,
    icons
  };
};

export const IconPicker: React.FC<IconPickerProps> = ({ onChange }) => {
  const { search, setSearch, icons } = useIconPicker();
  const classes = useStyles();

  return (
    <Box>
      <TextField
        placeholder={iconPickerConfig.searchPlaceholder}
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        className={classes.searchField}
      />
      <Box className={classes.iconGrid}>
        <Grid container spacing={1}>
          {icons.map(({ name, Component }) => (
            <Grid item key={name}>
              <IconButton
                onClick={() => onChange(name)}
                className={classes.iconButton}
                title={name}
              >
                <Component className={classes.icon} />
              </IconButton>
            </Grid>
          ))}
        </Grid>
        {icons.length === 0 && (
          <Box className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {iconPickerConfig.noIconsFoundText}
            </Typography>
            <Button onClick={() => setSearch('')} color="primary">
              {iconPickerConfig.clearSearchText}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

function IconPickerDialog({
  selected,
  onChange,
  buttonText,
  dialogTitle = iconPickerConfig.dialogTitle,
  dialogDescription = iconPickerConfig.dialogDescription,
  renderTrigger
}: IconPickerDialogProps) {
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  const handleIconSelect = (icon: string) => {
    onChange(icon);
    setOpen(false);
  };

  const handleOpenDialog = () => setOpen(true);

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleOpenDialog)
      ) : (
        <Button
          variant="outlined"
          onClick={handleOpenDialog}
          className={classes.triggerButton}
        >
          {selected ? (
            <>
              <IconRenderer 
                icon={selected} 
                style={{ width: '16px', height: '16px', marginRight: '8px' }} 
              />
              {buttonText || iconPickerConfig.updateButtonText}
            </>
          ) : (
            buttonText || iconPickerConfig.selectButtonText
          )}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="body"
        aria-labelledby="icon-picker-dialog-title"
      >
        <DialogTitle disableTypography>
          <Typography className={classes.dialogTitle}>{dialogTitle}</Typography>
          <Typography variant="body2" color="textSecondary">
            {dialogDescription}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <IconPicker onChange={handleIconSelect} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            {iconPickerConfig.cancelButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default IconPickerDialog;

export { IconPickerDialog, iconPickerConfig, iconRegistry, useIconPicker };